import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  TemplateCategoryMasterCreateRequestDto,
  TemplateCategoryMasterDto,
  TemplateCategoryMasterUpdateRequestDto,
} from "@/dto/TemplateCategoryDto";
import useTemplateCategory from "@/pages/Template/Detail/useTemplateCategory";
import { useSnackbar } from "notistack";

export default function useCategoryForm() {
  // 카테고리 타입 (1: 페이지, 2: 섹션)
  const [categoryType, setCategoryType] = useState<number>(1);

  // 수정 모달 상태
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);

  // 트리 항목 확장 상태
  const [openItems, setOpenItems] = useState<Record<number, boolean>>({});

  // 템플릿 카테고리 관련 기능 및 상태 가져오기
  const {
    isLoading,
    pageCategories = [], // 기본값으로 빈 배열 설정
    getSectionsByPageId,
    selectedCategory,
    setSelectedCategory,
    handleCategoryCreate,
    handleCategoryUpdate,
    handleCategoryDelete,
    forceRefresh, // 강제 새로고침 함수만 사용
  } = useTemplateCategory();

  const { enqueueSnackbar } = useSnackbar();

  // 생성 폼 초기화
  const createForm = useForm<TemplateCategoryMasterCreateRequestDto>({
    defaultValues: {
      templateId: 0,
      categoryLevel: 1,
      categoryName: "",
      categoryDesc: "",
      dispOrder: 0,
      demoPageUrl: "",
      anchorId: "",
      locationDesc: "",
      screenshotUrl: "",
      parentMasterCategoryId: null, // null로 명시적 초기화
    },
  });

  // 카테고리 타입이 변경될 때 폼 필드 조정
  useEffect(() => {
    if (categoryType === 1) {
      // 페이지인 경우 parentMasterCategoryId를 null로 설정
      createForm.setValue("parentMasterCategoryId", null);
    } else if (categoryType === 2 && pageCategories.length > 0) {
      // 섹션으로 변경되었을 때, parentMasterCategoryId가 없거나 유효하지 않은 경우만 기본값 설정
      const currentParentId = createForm.getValues("parentMasterCategoryId");
      const isValidParentId =
        currentParentId &&
        pageCategories.some(
          (page) => page.masterCategoryId === Number(currentParentId)
        );

      if (!isValidParentId) {
        console.log(
          "섹션 타입으로 변경됨, 상위 페이지 ID 설정:",
          pageCategories[0].masterCategoryId
        );
        createForm.setValue(
          "parentMasterCategoryId",
          pageCategories[0].masterCategoryId
        );
      }
    }
  }, [categoryType, pageCategories, createForm]);

  // 페이지 목록이 변경될 때마다 현재 선택된 상위 페이지가 유효한지 확인
  useEffect(() => {
    if (categoryType === 2 && pageCategories.length > 0) {
      const currentParentId = createForm.getValues("parentMasterCategoryId");
      const isValidParentId =
        currentParentId &&
        pageCategories.some(
          (page) => page.masterCategoryId === Number(currentParentId)
        );

      if (!isValidParentId) {
        console.log(
          "페이지 목록 변경됨, 상위 페이지 ID 설정:",
          pageCategories[0].masterCategoryId
        );
        createForm.setValue(
          "parentMasterCategoryId",
          pageCategories[0].masterCategoryId
        );
      }
    }
  }, [pageCategories, categoryType, createForm]);

  // 카테고리 레벨 변경 핸들러
  const handleCategoryTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value);
    setCategoryType(value);
    createForm.setValue("categoryLevel", value);

    // 필드 초기화
    if (value === 1) {
      // 페이지 타입으로 변경 시 섹션 관련 필드 초기화
      createForm.setValue("parentMasterCategoryId", null);
      createForm.setValue("categoryDesc", "");
      createForm.setValue("anchorId", "");
      createForm.setValue("locationDesc", "");
      createForm.setValue("screenshotUrl", "");
    } else {
      // 섹션 타입으로 변경 시 페이지 관련 필드 초기화
      createForm.setValue("demoPageUrl", "");
      // 페이지가 있는 경우 첫 번째 페이지 선택
      if (pageCategories.length > 0) {
        console.log(
          "섹션 타입으로 변경됨, 상위 페이지 명시적 설정:",
          pageCategories[0].masterCategoryId
        );
        createForm.setValue(
          "parentMasterCategoryId",
          pageCategories[0].masterCategoryId
        );
      }
    }
  };

  // 수정 폼 제출 핸들러
  const handleEditSubmit = async (
    data: TemplateCategoryMasterUpdateRequestDto
  ) => {
    if (!selectedCategory) {
      enqueueSnackbar("선택된 카테고리 정보가 없습니다.", { variant: "error" });
      return false;
    }

    try {
      const updateData = {
        ...data,
        masterCategoryId: Number(selectedCategory.masterCategoryId),
        // 숫자 필드 명시적 변환
        dispOrder: Number(data.dispOrder),
      };

      console.log("카테고리 수정 요청 데이터:", updateData);

      const success = await handleCategoryUpdate(updateData);
      if (success) {
        setEditDialogOpen(false);
        enqueueSnackbar("카테고리가 성공적으로 수정되었습니다.", {
          variant: "success",
        });
        return true;
      } else {
        enqueueSnackbar("카테고리 수정에 실패했습니다.", { variant: "error" });
        return false;
      }
    } catch (error) {
      console.error("카테고리 수정 에러:", error);
      enqueueSnackbar(
        `카테고리 수정 실패: ${
          error instanceof Error ? error.message : "알 수 없는 오류"
        }`,
        {
          variant: "error",
        }
      );
      return false;
    }
  };

  // 생성 폼 제출 핸들러
  const handleCreateSubmit = async (
    data: TemplateCategoryMasterCreateRequestDto
  ) => {
    // API 요구사항에 맞게 데이터 조정
    const requestData = { ...data };
    console.log("제출 전 원본 데이터:", JSON.stringify(data));

    // 현재 카테고리 타입에 맞게 categoryLevel 명시적 설정
    requestData.categoryLevel = categoryType;
    console.log("카테고리 레벨 명시적 설정:", categoryType);

    // 페이지인 경우 (카테고리 레벨 1)
    if (categoryType === 1) {
      // 레벨 1(페이지)인 경우 parentMasterCategoryId 속성 자체를 제거
      delete requestData.parentMasterCategoryId;
    } else {
      // 섹션인 경우 (카테고리 레벨 2)
      // 페이지가 없으면 등록 불가능
      if (pageCategories.length === 0) {
        enqueueSnackbar("페이지를 먼저 등록해야 섹션을 추가할 수 있습니다.", {
          variant: "error",
        });
        return;
      }

      // 부모 카테고리 ID가 없거나 유효하지 않은 경우 첫 번째 페이지 ID로 설정
      if (!requestData.parentMasterCategoryId) {
        console.log(
          "제출 시 상위 페이지 ID 없음, 첫 번째 페이지로 설정:",
          pageCategories[0].masterCategoryId
        );
        requestData.parentMasterCategoryId = pageCategories[0].masterCategoryId;
      } else {
        // 문자열로 된 값을 숫자로 명시적 변환
        requestData.parentMasterCategoryId = Number(
          requestData.parentMasterCategoryId
        );
        console.log(
          "제출 시 상위 페이지 ID 설정:",
          requestData.parentMasterCategoryId
        );
      }
    }

    // 다른 숫자 필드들도 명시적으로 숫자 타입으로 변환
    requestData.templateId = Number(requestData.templateId);
    // 카테고리 타입에 따라 categoryLevel 값 재확인 (디버깅용)
    console.log(
      `카테고리 타입: ${categoryType}, 설정된 레벨: ${requestData.categoryLevel}`
    );
    requestData.dispOrder = Number(requestData.dispOrder);

    console.log("카테고리 생성 요청 데이터:", requestData);

    const success = await handleCategoryCreate(requestData);
    if (success) {
      // 폼 리셋 시 categoryLevel 값이 기본값(1)으로 돌아가는 문제 방지
      const currentType = categoryType;
      createForm.reset();

      // 폼 리셋 후에도 현재 카테고리 타입 유지
      createForm.setValue("categoryLevel", currentType);

      // 섹션 등록 후에도 상위 페이지 선택 유지
      if (currentType === 2 && pageCategories.length > 0) {
        const currentParentId = Number(data.parentMasterCategoryId);
        createForm.setValue("parentMasterCategoryId", currentParentId);
      }

      // 데이터가 즉시 반영되지 않는 문제 해결을 위해 여러 번 데이터 새로고침 시도
      console.log("카테고리 등록 성공, 강제 새로고침 시작");

      // 강제 새로고침으로 변경
      forceRefresh();

      // 시간차를 두고 추가로 데이터 불러오기 (서버 반영 지연 대응)
      setTimeout(() => {
        console.log("1초 후 카테고리 목록 강제 새로고침");
        forceRefresh();
      }, 1000);
    }
  };

  // 수정 모달 열기
  const handleEditOpen = (category: TemplateCategoryMasterDto) => {
    setSelectedCategory(category);

    // 폼 초기화
    createForm.reset({
      categoryName: category.categoryName,
      categoryDesc: category.categoryDesc || "",
      dispOrder: Number(category.dispOrder),
      demoPageUrl: category.demoPageUrl || "",
      anchorId: category.anchorId || "",
      locationDesc: category.locationDesc || "",
      screenshotUrl: category.screenshotUrl || "",
    });

    setEditDialogOpen(true);
  };

  // 삭제 핸들러
  const handleDelete = (
    categoryId: number,
    categoryName: string,
    isPage: boolean
  ) => {
    // 숫자형으로 확실히 변환
    handleCategoryDelete(categoryId, categoryName, isPage);
  };

  // 트리 아이템 토글 핸들러
  const handleToggle = (id: number) => {
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return {
    // 상태
    isLoading,
    categoryType,
    openItems,
    editDialogOpen,
    pageCategories,
    selectedCategory,

    // 폼
    createForm,

    // 핸들러
    handleCategoryTypeChange,
    handleCreateSubmit,
    handleEditOpen,
    handleEditSubmit,
    handleDelete,
    handleToggle,
    setEditDialogOpen,

    // 유틸리티
    getSectionsByPageId,
    forceRefresh,
  };
}
